/**
 * PendingSection - Pending parlays and pending AI predictions display components
 */
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { Clock } from 'lucide-react';
import type { ReactElement } from 'react';
import type {
  PendingLegInfo,
  PendingParlayDetail,
  PendingPredictionDetailGroup,
  PendingPredictionLegInfo,
} from '../../hooks/useLabDashboard';

function PendingLegRow({ leg }: { leg: PendingLegInfo }): ReactElement {
  // Determine if this is a game prop based on whether there's a player
  const isGameProp = !leg.player || leg.player === 'Unknown';

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      {/* Status indicator - clock icon for pending */}
      <div className="w-6 h-6 rounded-full bg-theme-primary-fill flex items-center justify-center shrink-0">
        <Clock className="w-3.5 h-3.5 text-theme-on-primary-fill" />
      </div>

      {/* Player/Team and prop info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white truncate">
            {isGameProp ? leg.team || 'Game Prop' : leg.player}
          </span>
          {!isGameProp && leg.team && (
            <span className="text-xs text-theme-text-tertiary shrink-0">({leg.team})</span>
          )}
        </div>
        <div className="text-sm text-theme-text-secondary">{leg.prop}</div>
      </div>

      {/* Odds and game status */}
      <div className="text-right shrink-0">
        {leg.odds && <div className="text-sm font-medium text-white">{leg.odds}</div>}
        <div className="text-xs text-theme-primary">{leg.gameStatus}</div>
      </div>
    </div>
  );
}

function PendingParlayCard({ parlay }: { parlay: PendingParlayDetail }): ReactElement {
  return (
    <div className="bg-theme-surface rounded-lg border border-theme-primary/30/50 overflow-hidden">
      {/* Parlay Header */}
      <div className="p-4 border-b border-theme-border">
        {/* User Display Name and Email row */}
        <div className="flex items-center gap-2 mb-2 text-sm">
          <span className="text-white font-medium">{parlay.userDisplayName || 'System'}</span>
          {parlay.userEmail && (
            <span className="text-theme-text-tertiary">({parlay.userEmail})</span>
          )}
        </div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 text-xs font-medium rounded-full border bg-theme-primary/50 text-blue-300 border-theme-primary/30">
              PENDING
            </span>
            {parlay.tier && (
              <span className="text-xs text-theme-text-tertiary bg-theme-surface-hover px-2 py-0.5 rounded">
                {parlay.tier}
              </span>
            )}
            <span className="text-xs text-theme-text-tertiary">
              {parlay.legs.length} leg{parlay.legs.length !== 1 ? 's' : ''} awaiting results
            </span>
          </div>
          {parlay.totalOdds && (
            <div className="text-right">
              <span className="text-sm font-medium text-white">{parlay.totalOdds}</span>
            </div>
          )}
        </div>
      </div>

      {/* Legs */}
      <div className="divide-y divide-gray-700/50">
        {parlay.legs.map((leg, idx) => (
          <PendingLegRow key={idx} leg={leg} />
        ))}
      </div>
    </div>
  );
}

export function PendingParlaysSection({
  pendingParlays,
}: {
  pendingParlays: PendingParlayDetail[];
}): ReactElement {
  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-theme-primary" />
        <span className="text-sm text-blue-300 font-medium">Awaiting Game Results</span>
      </div>

      {/* Parlay cards */}
      <div className="space-y-4">
        {pendingParlays.map((parlay) => (
          <PendingParlayCard key={parlay.parlayId} parlay={parlay} />
        ))}
      </div>
    </div>
  );
}

function PendingPredictionLegRow({ leg }: { leg: PendingPredictionLegInfo }): ReactElement {
  // Format prop display
  const propDisplay =
    leg.overUnder && leg.line
      ? `${leg.overUnder} ${leg.line} ${formatStatLabel(leg.propType)}`
      : `${formatStatLabel(leg.propType)} ${leg.line || ''}`;

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      {/* Status indicator - clock icon for pending */}
      <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center shrink-0">
        <Clock className="w-3.5 h-3.5 text-white" />
      </div>

      {/* Player and prop info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white truncate">{leg.playerName || 'Unknown'}</span>
          <span className="text-xs text-theme-text-tertiary shrink-0">({leg.team})</span>
        </div>
        <div className="text-sm text-theme-text-secondary">{propDisplay}</div>
      </div>
    </div>
  );
}

function PendingPredictionCard({
  prediction,
}: {
  prediction: PendingPredictionDetailGroup;
}): ReactElement {
  // Format date
  const dateStr = prediction.gameDate
    ? new Date(prediction.gameDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  return (
    <div className="bg-theme-surface rounded-lg border border-purple-700/30 overflow-hidden">
      {/* Prediction Header */}
      <div className="p-4 border-b border-theme-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 text-xs font-medium rounded-full border bg-purple-900/50 text-purple-300 border-purple-700">
              PENDING
            </span>
            <span className="text-xs text-purple-400 bg-purple-900/30 px-2 py-0.5 rounded border border-purple-700/50">
              {prediction.aiTier}
            </span>
            {dateStr && <span className="text-xs text-theme-text-tertiary">{dateStr}</span>}
          </div>
          <span className="text-xs text-theme-text-tertiary">
            {prediction.legs.length} pick
            {prediction.legs.length !== 1 ? 's' : ''} awaiting results
          </span>
        </div>
      </div>

      {/* Legs */}
      <div className="divide-y divide-gray-700/50">
        {prediction.legs.map((leg, idx) => (
          <PendingPredictionLegRow key={idx} leg={leg} />
        ))}
      </div>
    </div>
  );
}

export function PendingPredictionsSection({
  pendingPredictions,
}: {
  pendingPredictions: PendingPredictionDetailGroup[];
}): ReactElement {
  const totalLegs = pendingPredictions.reduce((sum, p) => sum + p.legs.length, 0);

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-purple-400" />
        <span className="text-sm text-purple-300 font-medium">
          AI Predictions Awaiting Results ({totalLegs} picks)
        </span>
      </div>

      {/* Prediction cards */}
      <div className="space-y-4">
        {pendingPredictions.map((prediction, idx) => (
          <PendingPredictionCard key={`${prediction.aiTier}-${idx}`} prediction={prediction} />
        ))}
      </div>
    </div>
  );
}
